import { PropertyTypeEnum } from 'api/real-estate';
import { BranchCode } from 'api/risk';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { RequiredTabsConfig, TabFieldsConfig, useTabErrors } from 'components/navigation/Tabs/useTabErrors';
import { BaseSection, InputContainer } from 'components/layout/Section/BaseSection';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { setAnswer } from 'features/subscription-process/profiling-process/slices/genericProfilingSlice';
import { IconPossibleAnswer } from 'features/subscription-process/profiling-process/types/interfaces';
import { selectAllQuestions } from 'features/subscription-process/profiling-process/slices/genericSelectors';
import { isIconAnswer, isLabelAnswer } from 'features/subscription-process/profiling-process/types/possibleAnswer';
import { useFormikContext } from 'formik';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { realEstateInputsConfig } from '../../form/realEstateInputsConfig';
import { RealEstateFormFieldNames } from '../../types/realEstateFormFieldNames';
import { UpiRealEstateFormValues } from '../../types/realEstateFormValues';
import { RealEstateSurveyLayout } from './RealEstateSurveyLayout';

const PROPERTY_TYPE_QUESTION_KEY = "typeBien";

const PROPERTY_TYPE_TO_SURVEY_ANSWER: Record<number, IconPossibleAnswer> = {
  [PropertyTypeEnum.House]: { key: 1, label: "Une maison", iconKey: "house-blue" },
  [PropertyTypeEnum.Apartment]: { key: 2, label: "Un appartement", iconKey: "building" },
  [PropertyTypeEnum.Office]: { key: 3, label: "Un garage", iconKey: "garage" },
  [PropertyTypeEnum.Commercial]: { key: 4, label: "Un local commercial", iconKey: "garage" },
};

export const realEstateTabFieldsConfig: TabFieldsConfig<UpiRealEstateFormValues> = {
  1: [
    RealEstateFormFieldNames.Name,
    RealEstateFormFieldNames.Area,
    RealEstateFormFieldNames.MonthlyRent,
    RealEstateFormFieldNames.Charges,
    RealEstateFormFieldNames.Photo,
    AddressFormFieldNames.Address,
    AddressFormFieldNames.AdditionalAddress,
    AddressFormFieldNames.PostalCode,
    AddressFormFieldNames.City,
    AddressFormFieldNames.Country
  ],
  2: [
    RealEstateFormFieldNames.SurveyForm
  ]
};

export const realEstateRequiredTabsConfig: RequiredTabsConfig = {
  2: true
};

interface RealEstateLayoutProps {
  branchCode: BranchCode
}

export const RealEstateLayout: React.FC<RealEstateLayoutProps> = ({ branchCode }) => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const dispatch = useAppDispatch();
  const questions = useAppSelector(selectAllQuestions);
  const [active, setActive] = useState<number | string>(1);
  const { errors, setFieldTouched, values } = useFormikContext<UpiRealEstateFormValues>();
  const lastSyncedRef = useRef<string | null>(null);

  // Sync propertyType (tab 1) → typeBien (survey Redux).
  // On résout la VRAIE réponse possible du questionnaire (avec son id) plutôt que l'objet codé
  // en dur sans id : sinon la réponse est sauvegardée en "[object Object]" sans reponsePossibleId,
  // et le moteur d'éligibilité CBNO ne peut pas la lire (bien marqué « à compléter » à tort).
  useEffect(() => {
    if (branchCode !== BranchCode.UPI) return;
    const currentPropertyType = values.propertyType;
    if (currentPropertyType == null) return;

    const surveyAnswer = PROPERTY_TYPE_TO_SURVEY_ANSWER[currentPropertyType];
    if (!surveyAnswer) return;

    const typeBienQuestion = questions.find((q) => q.key === PROPERTY_TYPE_QUESTION_KEY);
    const matchedAnswer = typeBienQuestion?.possibleAnswers.find(
      (pa) => (isIconAnswer(pa) || isLabelAnswer(pa)) && String(pa.key) === String(surveyAnswer.key)
    );
    const resolved = matchedAnswer ?? surveyAnswer;

    // Idempotent ; se redéclenche quand les questions (donc l'id réel) sont chargées.
    const signature = `${currentPropertyType}:${(resolved as IconPossibleAnswer & { id?: string }).id ?? 'noid'}`;
    if (signature === lastSyncedRef.current) return;
    lastSyncedRef.current = signature;

    dispatch(setAnswer({ questionKey: PROPERTY_TYPE_QUESTION_KEY, value: [resolved] }));
  }, [branchCode, dispatch, values.propertyType, questions]);

  const { getTabNotification, touchFieldsWithErrors, touchFieldsWithErrorsInTab } = useTabErrors(
    errors,
    realEstateTabFieldsConfig,
    realEstateRequiredTabsConfig,
    setFieldTouched
  );

  // Marquer les champs avec erreurs comme touched au montage et quand les erreurs changent
  useEffect(() => {
    touchFieldsWithErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleTabChange = useCallback((tabValue: number | string) => {
    setActive(tabValue);
    touchFieldsWithErrorsInTab(tabValue);
  }, [touchFieldsWithErrorsInTab]);

  const renderField = useCallback((fieldName: any) => {
    return (
      <InputFactory config={realEstateInputsConfig} fieldName={fieldName} />
    );
  }, []);

  const renderForm = useMemo(() => {
    return <div className="font-lato flex flex-col gap-4">
      <BaseSection title={t('REAL_ESTATE.SECTIONS.GENERAL_INFO')}>
        <div className='pt-2'>
          <InputContainer>
            <div className="col-span-3 flex items-center justify-center">
              {renderField(RealEstateFormFieldNames.Photo)}
            </div>
            <div className="col-span-3 flex flex-col gap-4">
              {renderField(RealEstateFormFieldNames.Name)}
              {renderField(RealEstateFormFieldNames.Area)}
              {renderField(RealEstateFormFieldNames.RoomCount)}
            </div>
          </InputContainer>
        </div>
      </BaseSection>
      <BaseSection title={t('REAL_ESTATE.SECTIONS.FINANCIAL_INFO')}>
        <div className='space-y-4'>
          {
            branchCode === BranchCode.RGI &&
            <div className="col-span-3">
              {renderField(RealEstateFormFieldNames.RentalType)}
            </div>
          }
          <div className="col-span-3">
            {renderField(RealEstateFormFieldNames.PropertyType)}
          </div>
          <div className="col-span-6 grid grid-cols-2 gap-4">
            {renderField(RealEstateFormFieldNames.MonthlyRent)}
            {renderField(RealEstateFormFieldNames.Charges)}
          </div>
          <p className="text-sm text-gray-500 italic">
            {t('REAL_ESTATE.MESSAGES.RENT_CHARGES_HINT')}
          </p>
        </div>
      </BaseSection>
      <BaseSection title={t('COMMON.LABELS.COORDINATE')}>
        <InputContainer>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.Address)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.AdditionalAddress)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.PostalCode)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.City)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.Country)}
          </div>
        </InputContainer>
      </BaseSection>
    </div>
  }, [branchCode, renderField, t])

  if (branchCode === BranchCode.RGI) {
    return renderForm
  }

  return (
    <GenericTabs defaultTab={active} onChange={handleTabChange}>
      <GenericTabs.Item
        label={t("REAL_ESTATE.SECTIONS.REAL_ESTATE_INFOS")}
        value={1}
        notification={getTabNotification(1)}
      />
      <GenericTabs.Item
        label={t("REAL_ESTATE.SECTIONS.PROPERTY_SITUATION")}
        value={2}
        notification={getTabNotification(2)}
      />
      <GenericTabs.Content value={1}>
        {renderForm}
      </GenericTabs.Content>
      <GenericTabs.Content value={2}>
        <RealEstateSurveyLayout />
      </GenericTabs.Content>
    </GenericTabs>
  );
};
